<template>
    <div v-if="displayActions.length > 0 || allowAddAction" class="mt-2 flex flex-col gap-1">
        <div class="flex items-center justify-between gap-2 mb-1">
            <p class="scaffold-body-small text-ink-muted">Actions</p>
            <BaseButton v-if="allowAddAction" theme="tertiary" size="sm" class="inline-flex items-center gap-1" @click="addActionVisible ? closeAddAction() : openAddAction()">
                <i class="bi bi-plus text-base" aria-hidden="true"></i>
                Add action
            </BaseButton>
        </div>

        <ul v-if="displayActions.length > 0" class="flex flex-col gap-2">
            <li v-for="action in displayActions" :key="action.id" class="group">
                <!-- Edit mode -->
                <GoalActionForm
                    v-if="editingActionId === action.id"
                    :initial-title="editingActionTitle"
                    :initial-description="editingActionDescription"
                    :is-submitting="isEditingAction"
                    title-aria-label="editing-action-title"
                    description-aria-label="editing-action-description"
                    @submit="
                        (t: string, d: string) => {
                            editingActionTitle = t;
                            editingActionDescription = d;
                            submitEditAction();
                        }
                    "
                    @cancel="cancelEditAction" />

                <!-- View mode -->
                <BaseCard v-else :theme="action.status === 'completed' ? 'muted' : 'default'" :data-goal-action-id="action.id" padding="sm" class="flex flex-col gap-2">
                    <div class="flex items-center gap-2.5">
                        <BaseButton
                            v-if="allowAddAction"
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :aria-label="action.status === 'completed' ? `Mark ${action.title} not complete` : `Mark ${action.title} complete`"
                            @click="handleToggleActionStatus(action.id)">
                            <i class="bi" :class="action.status === 'completed' ? 'bi-check-circle-fill' : 'bi-circle'" aria-hidden="true"></i>
                        </BaseButton>
                        <i
                            v-else
                            class="bi shrink-0 text-sm"
                            :class="action.status === 'completed' ? 'bi-check-circle-fill text-chip-status-done-text' : 'bi-circle text-ink-muted'"
                            aria-hidden="true"></i>
                        <span class="flex-1 min-w-0 body-small" :class="action.status === 'completed' ? 'text-ink-muted line-through' : 'text-ink'">
                            {{ action.title }}
                        </span>
                        <BaseButton
                            v-if="hasDescription(action)"
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :aria-label="isExpanded(action.id) ? 'Collapse action details' : 'Expand action details'"
                            :aria-expanded="isExpanded(action.id)"
                            @click="toggleExpanded(action.id)">
                            <i class="bi text-xs" :class="isExpanded(action.id) ? 'bi-chevron-up' : 'bi-chevron-down'" aria-hidden="true"></i>
                        </BaseButton>
                        <BaseDropdownMenu
                            v-if="allowAddAction && action.status !== 'completed'"
                            :sections="actionMenuSections"
                            :side-offset="4"
                            @select="(item) => handleActionMenuSelect(item, action)"
                            @close-auto-focus="handleActionMenuCloseAutoFocus">
                            <template #trigger>
                                <BaseButton theme="tertiary" size="sm" icon-only aria-label="More actions">
                                    <i class="bi bi-three-dots text-xs" aria-hidden="true"></i>
                                </BaseButton>
                            </template>
                        </BaseDropdownMenu>
                    </div>
                    <div v-if="isExpanded(action.id) && hasDescription(action)" class="pl-6">
                        <span class="body-small text-ink-muted">{{ action.description }}</span>
                    </div>
                </BaseCard>
            </li>
        </ul>

        <!-- Add row -->
        <GoalActionForm
            v-if="allowAddAction && addActionVisible"
            ref="addFormRef"
            class="mt-1"
            :is-submitting="isAddingAction"
            title-aria-label="new-action-title"
            description-aria-label="new-action-description"
            @submit="handleAddSubmit"
            @cancel="closeAddAction" />
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import { type GoalAction, type GoalActionCreateRequest, type GoalActionUpdateRequest, GoalStatus } from "~vue/types/goals";
import { computed, nextTick, ref } from "vue";

import GoalActionForm from "./GoalActionForm.vue";
import { JourneysAmplitudeEventNames, logJourneysUserInteraction } from "./utils";

const props = defineProps<{
    goalId: string;
    actions: GoalAction[];
    allowAddAction?: boolean;
}>();

const emit = defineEmits<{ changed: []; "all-completed": []; "request-delete-confirm": [actionId: string] }>();

const actionMenuSections: DropdownMenuSection[] = [
    {
        id: "actions",
        items: [
            { id: "edit", type: "button", label: "Edit", icon: "bi bi-pencil" },
            { id: "delete", type: "button", label: "Delete", icon: "bi bi-trash" },
        ],
    },
];
const isOpeningDeleteDialog = ref(false);

const handleActionMenuSelect = (item: DropdownMenuItem, action: GoalAction) => {
    if (item.id === "edit") {
        startEditAction(action);
    } else if (item.id === "delete") {
        isOpeningDeleteDialog.value = true;
        emit("request-delete-confirm", action.id);
    }
};

function handleActionMenuCloseAutoFocus(event: Event) {
    if (isOpeningDeleteDialog.value) {
        event.preventDefault();
        isOpeningDeleteDialog.value = false;
    }
}

const optimisticStatuses = ref(new Map<string, GoalStatus>());
const optimisticDeletedIds = ref(new Set<string>());
const displayActions = computed(() =>
    props.actions
        .filter((action) => !optimisticDeletedIds.value.has(action.id))
        .map((action) => {
            const override = optimisticStatuses.value.get(action.id);
            return override !== undefined ? { ...action, status: override } : action;
        }),
);

const allActionsCompleted = computed(() => displayActions.value.length > 0 && displayActions.value.every((a) => a.status === GoalStatus.Completed));

// Expand/collapse details (description + edit/delete) per action
const expandedActionIds = ref<Set<string>>(new Set());

function isExpanded(actionId: string): boolean {
    return expandedActionIds.value.has(actionId);
}

function toggleExpanded(actionId: string) {
    const next = new Set(expandedActionIds.value);
    if (next.has(actionId)) {
        next.delete(actionId);
    } else {
        next.add(actionId);
    }
    expandedActionIds.value = next;
}

function hasDescription(action: GoalAction): boolean {
    return action.status !== GoalStatus.Completed && !!action.description?.trim();
}

// Add action state
const addActionVisible = ref(false);
const isAddingAction = ref(false);
const addFormRef = ref<InstanceType<typeof GoalActionForm> | null>(null);

const newActionPayload = ref<GoalActionCreateRequest>({ title: "", description: "" });
const { execute: postAction } = useFetch(
    computed(() => `/v1/goals/${props.goalId}/actions`),
    { immediate: false },
)
    .post(newActionPayload)
    .json();

async function openAddAction() {
    addActionVisible.value = true;
    await nextTick();
    addFormRef.value?.focus();
}

function closeAddAction() {
    addActionVisible.value = false;
}

async function handleAddSubmit(title: string, description: string) {
    isAddingAction.value = true;
    newActionPayload.value = { title, description };
    await postAction();
    isAddingAction.value = false;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_ACTION_ADDED, { goal_id: props.goalId });
    emit("changed");
    closeAddAction();
}

// Edit/complete/delete state
const editingActionId = ref<string | null>(null);
const editingActionTitle = ref("");
const editingActionDescription = ref("");
const isEditingAction = ref(false);
const activeActionId = ref<string | null>(null);

const editActionPayload = computed<GoalActionUpdateRequest>(() => ({ title: editingActionTitle.value, description: cleanDescription(editingActionDescription.value) }));

const { execute: patchAction } = useFetch(
    computed(() => `/v1/goals/${props.goalId}/actions/${activeActionId.value}`),
    { immediate: false },
)
    .patch(editActionPayload)
    .json();

const toggleTargetStatus = ref<GoalStatus>(GoalStatus.Active);
const toggleStatusPayload = computed<GoalActionUpdateRequest>(() => ({ status: toggleTargetStatus.value }));

const { execute: toggleActionStatus, error: toggleError } = useFetch(
    computed(() => `/v1/goals/${props.goalId}/actions/${activeActionId.value}`),
    { immediate: false },
)
    .patch(toggleStatusPayload)
    .json();

const { execute: deleteAction, error: deleteError } = useFetch(
    computed(() => `/v1/goals/${props.goalId}/actions/${activeActionId.value}`),
    { immediate: false },
).delete();

function cleanDescription(value: string): string {
    return value.replace(/\s+/g, " ").trim();
}

function startEditAction(action: GoalAction) {
    editingActionId.value = action.id;
    editingActionTitle.value = action.title;
    editingActionDescription.value = cleanDescription(action.description);
}

function cancelEditAction() {
    editingActionId.value = null;
    editingActionTitle.value = "";
    editingActionDescription.value = "";
}

async function submitEditAction() {
    if (!editingActionTitle.value.trim() || isEditingAction.value) {
        return;
    }
    isEditingAction.value = true;
    activeActionId.value = editingActionId.value;
    await patchAction();
    isEditingAction.value = false;
    cancelEditAction();
    emit("changed");
}

async function handleToggleActionStatus(actionId: string) {
    const action = props.actions.find((a) => a.id === actionId);
    if (!action) {
        return;
    }

    const currentStatus = optimisticStatuses.value.get(actionId) ?? action.status;
    const isCompleting = currentStatus !== GoalStatus.Completed;
    const newStatus = isCompleting ? GoalStatus.Completed : GoalStatus.Active;

    activeActionId.value = actionId;
    toggleTargetStatus.value = newStatus;
    optimisticStatuses.value = new Map(optimisticStatuses.value).set(actionId, newStatus);

    await toggleActionStatus();

    if (toggleError.value) {
        const reverted = new Map(optimisticStatuses.value);
        reverted.delete(actionId);
        optimisticStatuses.value = reverted;
        return;
    }

    if (isCompleting) {
        logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_ACTION_COMPLETED, {
            goal_id: props.goalId,
            action_id: actionId,
        });

        if (allActionsCompleted.value) {
            emit("all-completed");
        }
    }
    emit("changed");
}

async function executeDelete(actionId: string) {
    activeActionId.value = actionId;
    optimisticDeletedIds.value = new Set(optimisticDeletedIds.value).add(actionId);

    await deleteAction();

    if (deleteError.value) {
        const reverted = new Set(optimisticDeletedIds.value);
        reverted.delete(actionId);
        optimisticDeletedIds.value = reverted;
        throw new Error("Couldn't delete action.");
    }

    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_ACTION_DELETED, {
        goal_id: props.goalId,
        action_id: actionId,
    });

    if (allActionsCompleted.value) {
        emit("all-completed");
    }
    emit("changed");
}

defineExpose({ executeDelete });
</script>
